import React, { useEffect, useRef, useState } from "react";
import { FaBars, FaCaretDown, FaTimes } from "react-icons/fa";
import { NavLink, useLocation } from "react-router-dom";

// Add a new cohort here and it shows up in the dropdown automatically.
const cohorts = [
  { name: "Cohort 1", path: "/cohort-1" },
  { name: "Cohort 2", path: "/cohort-2" },
];

// Menu items either side of the Cohort dropdown.
const linksBeforeCohort = [
  { name: "About The Program", path: "/", end: true },
  { name: "The Challenge", path: "/challenge-background" },
  { name: "Solution Areas", path: "/solution-areas" },
];

const linksAfterCohort = [
  { name: "Eligibility", path: "/eligibility" },
  { name: "Why Join", path: "/why-join" },
  { name: "Timeline", path: "/timeline" },
  { name: "Partners", path: "/partners" },
  { name: "News and Events", path: "/news-events" },
  { name: "Resources", path: "/resources" },
  { name: "Contact", path: "/contact-us" },
];

// Full-width rows on mobile so the whole strip is tappable; underline the
// active item on desktop only, where the menu is a single row.
const activeClass = "font-bold md:border-b-2 md:border-black";

const linkClass = ({ isActive }) =>
  `w-full md:w-auto text-center py-3 md:py-0 ${isActive ? activeClass : ""}`;

export default function Navigation() {
  // Closed on mobile until tapped; always visible from md up.
  const [showNav, setShowNav] = useState(false);
  const [showCohorts, setShowCohorts] = useState(false);
  const cohortRef = useRef(null);
  const { pathname } = useLocation();

  const isCohortActive = cohorts.some((cohort) => cohort.path === pathname);

  // Close the cohort dropdown when clicking anywhere outside of it.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cohortRef.current && !cohortRef.current.contains(e.target)) {
        setShowCohorts(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Navigating away closes both the mobile menu and the dropdown.
  useEffect(() => {
    setShowNav(false);
    setShowCohorts(false);
  }, [pathname]);

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Mobile toggle. Sits on its own row so it never overlaps a link. */}
        <div className="flex justify-end py-3 md:hidden">
          <button
            type="button"
            aria-label={showNav ? "Close menu" : "Open menu"}
            aria-expanded={showNav}
            onClick={() => setShowNav((open) => !open)}
            className="p-2"
          >
            {showNav ? (
              <FaTimes className="fill-secondary w-6 h-6" />
            ) : (
              <FaBars className="fill-secondary w-6 h-6" />
            )}
          </button>
        </div>

        <div
          className={`${
            showNav ? "flex" : "hidden"
          } md:flex flex-col md:flex-row items-center justify-between flex-wrap gap-1 md:gap-4 pb-4 md:py-6 max-h-[70vh] md:max-h-none overflow-y-auto md:overflow-visible divide-y md:divide-y-0 divide-gray-100`}
        >
          {linksBeforeCohort.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              end={link.end}
              className={linkClass}
            >
              {link.name}
            </NavLink>
          ))}

          <div
            className="relative w-full md:w-auto flex flex-col items-center"
            ref={cohortRef}
          >
            <button
              type="button"
              aria-haspopup="true"
              aria-expanded={showCohorts}
              onClick={() => setShowCohorts((open) => !open)}
              className={`flex items-center justify-center gap-1 w-full md:w-auto py-3 md:py-0 ${
                isCohortActive ? activeClass : ""
              }`}
            >
              Cohort
              <FaCaretDown
                className={`transition-transform duration-200 ${
                  showCohorts ? "rotate-180" : ""
                }`}
              />
            </button>

            {showCohorts && (
              // Mobile: expands inline and pushes the rest of the list down.
              // Desktop: floats over the page, where there is no room to push.
              <div className="w-full md:w-auto md:absolute md:left-1/2 md:-translate-x-1/2 md:top-full md:mt-2 md:z-20 md:min-w-[140px] bg-gray-50 md:bg-white md:border md:border-gray-200 md:rounded-md md:shadow-lg md:py-2">
                {cohorts.map((cohort) => (
                  <NavLink
                    key={cohort.path}
                    to={cohort.path}
                    className={({ isActive }) =>
                      `block w-full px-4 py-3 md:py-2 text-center md:text-left whitespace-nowrap hover:bg-gray-100 ${
                        isActive ? "font-bold" : ""
                      }`
                    }
                  >
                    {cohort.name}
                  </NavLink>
                ))}
              </div>
            )}
          </div>

          {linksAfterCohort.map((link) => (
            <NavLink key={link.path} to={link.path} className={linkClass}>
              {link.name}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}
